/**
 * Settings Page - Hall Settings and Configuration
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, hallAPI } from '../../services/api';

const SettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hall, setHall] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    pincode: '',
    contact_person: '',
    phone: '',
    whatsapp: '',
    email: '',
    capacity: '',
    area_sqft: '',
    description: '',
    celebration_price: '',
    booking_start_time: '',
    booking_end_time: '',
  });

  useEffect(() => {
    fetchHallData();
  }, []);

  const fetchHallData = async () => {
    try {
      setLoading(true);

      // Try to get user's hall - first try the admin endpoint, then fallback to halls list
      let hallData = null;

      try {
        const response = await adminAPI.getHall();
        hallData = response.data;
      } catch (err) {
        // If admin/hall endpoint doesn't exist, try to get from halls list
        console.log('Admin hall endpoint not available, fetching from halls list');
        const hallsResponse = await hallAPI.getAll();
        const hallsData = Array.isArray(hallsResponse.data) ? hallsResponse.data : (hallsResponse.data.results || []);

        // Get the first hall (for now - you can enhance this later)
        if (hallsData.length > 0) {
          hallData = hallsData[0];
        } else {
          throw new Error('No halls found');
        }
      }

      if (!hallData) {
        throw new Error('No hall data available');
      }

      setHall(hallData);
      setFormData({
        name: hallData.name || '',
        address: hallData.address || '',
        city: hallData.city || '',
        pincode: hallData.pincode || '',
        contact_person: hallData.contact_person || '',
        phone: hallData.phone || '',
        whatsapp: hallData.whatsapp || '',
        email: hallData.email || '',
        capacity: hallData.capacity || '',
        area_sqft: hallData.area_sqft || '',
        description: hallData.description || '',
        celebration_price: hallData.celebration_price || '',
        booking_start_time: hallData.booking_start_time || '09:00',
        booking_end_time: hallData.booking_end_time || '23:00',
      });
    } catch (error) {
      console.error('Error fetching hall data:', error);
      alert('Failed to load hall information. You may need to create a hall first.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hall || !hall.id) {
      alert('No hall selected. Cannot save settings.');
      return;
    }

    try {
      setSaving(true);

      // Use admin API to update hall
      await adminAPI.updateHall(formData);

      alert('Hall settings updated successfully!');
      fetchHallData();
    } catch (error) {
      console.error('Error updating hall:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to update hall settings';
      alert(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hall Settings</h1>
            <p className="text-sm text-gray-600">Manage your hall information and configuration</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('general')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === 'general'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                General Information
              </button>
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === 'contact'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Contact Details
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === 'pricing'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pricing & Hours
              </button>
            </nav>
          </div>
        </div>

        {/* Settings Form */}
        <form onSubmit={handleSubmit}>
          {/* General Information Tab */}
          {activeTab === 'general' && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">General Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hall Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacity (guests) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Area (sq.ft)
                  </label>
                  <input
                    type="number"
                    name="area_sqft"
                    value={formData.area_sqft}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Describe your hall and its features..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Contact Details Tab */}
          {activeTab === 'contact' && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="Same as phone if not provided"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Pricing & Hours Tab */}
          {activeTab === 'pricing' && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Operating Hours</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price Per Day <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">₹</span>
                    <input
                      type="number"
                      name="celebration_price"
                      value={formData.celebration_price}
                      onChange={handleChange}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Base price for hall booking per day</p>
                </div>

                <div className="md:col-span-2">
                  <div className="h-px bg-gray-200 my-4"></div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booking Start Time
                  </label>
                  <input
                    type="time"
                    name="booking_start_time"
                    value={formData.booking_start_time}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">When can events start at your hall?</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booking End Time
                  </label>
                  <input
                    type="time"
                    name="booking_end_time"
                    value={formData.booking_end_time}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">When must events end at your hall?</p>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>

        {/* Hall Status Info */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Hall Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-blue-700">Status</p>
              <p className="text-lg font-semibold text-blue-900 capitalize">{hall?.status || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Subscription</p>
              <p className="text-lg font-semibold text-blue-900 capitalize">{hall?.subscription_status || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Listed on Website</p>
              <p className="text-lg font-semibold text-blue-900">{hall?.is_active ? 'Yes' : 'No'}</p>
            </div>
          </div>
          {!hall?.is_active && (
            <p className="text-sm text-blue-700 mt-4">
              Your hall is currently not visible to customers. Please ensure your status is 'active' and subscription is 'active' or 'trial'.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
